/**
 * Globale Schnellsuche (Strg+K) — Command-Palette über /vehicles/search.
 * Sucht unscharf über VIN, Kennzeichen, interne Nummer, Marke und Modell;
 * Treffer führen auf die Fahrzeug-Detailseite.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { vehicleService } from '../services';

interface SearchHit {
  id: string;
  vin: string;
  licensePlate?: string | null;
  internalNumber?: string | null;
  make?: string;
  model?: string;
  status?: string;
  currentStay?: { stayNumber: string; status: string } | null;
}

const STATUS_LABELS: Record<string, string> = {
  ANNOUNCED: 'Avisiert',
  INCOMING: 'Im Zulauf',
  ON_YARD: 'Auf dem Hof',
  IN_PROCESS: 'In Bearbeitung',
  QC_PENDING: 'QS ausstehend',
  READY: 'Bereit',
  OUTGOING: 'Im Auslauf',
  DEPARTED: 'Ausgecheckt',
};

export function GlobalSearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);

  // Beim Öffnen: leeres Feld, Fokus.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelected(0);
      // Nach dem Rendern fokussieren.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Entprellte Suche ab 2 Zeichen.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await vehicleService.search(q);
        setResults(((res.data as unknown as SearchHit[]) || []).slice(0, 20));
        setSelected(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  if (!open) return null;

  const openVehicle = (hit: SearchHit) => {
    onClose();
    navigate(`/vehicles/${hit.id}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selected]) {
      openVehicle(results[selected]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-24 px-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl rounded-xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 border-b border-gray-200 dark:border-gray-700">
          <svg
            className="w-4 h-4 text-gray-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref={inputRef}
            className="flex-1 py-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
            placeholder="VIN, Kennzeichen, interne Nummer, Marke…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="px-2 py-0.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded">
            Esc
          </kbd>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {query.trim().length < 2 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">
              Mindestens 2 Zeichen eingeben…
            </p>
          ) : loading ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Suche…</p>
          ) : results.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">
              Keine Fahrzeuge für „{query.trim()}“ gefunden
            </p>
          ) : (
            <ul className="py-1">
              {results.map((hit, i) => (
                <li key={hit.id}>
                  <button
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                      i === selected
                        ? 'bg-primary-50 dark:bg-primary-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    onMouseEnter={() => setSelected(i)}
                    onClick={() => openVehicle(hit)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {hit.licensePlate || hit.vin}
                        </span>
                        {hit.internalNumber && (
                          <span className="text-xs text-primary-600 font-semibold shrink-0">
                            Nr. {hit.internalNumber}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {[hit.make, hit.model].filter(Boolean).join(' ')}
                        {hit.licensePlate ? ` · ${hit.vin}` : ''}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      {hit.status && (
                        <span className="text-xs text-gray-500">
                          {STATUS_LABELS[hit.status] || hit.status}
                        </span>
                      )}
                      {hit.currentStay?.status === 'OPEN' && (
                        <span className="text-[11px] text-primary-600 font-medium">
                          {hit.currentStay.stayNumber}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
